import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { CardAvatar as GenericCardAvatar } from '@sorare/core/src/components/card/Avatar';

import { LazyAllLastOwnedCardsByPlayer } from '#src/components/player/LazyAllLastOwnedCardsByPlayer/index.tsx';

import type { LastOwnedCards_anyPlayer } from './__generated__/index.graphql.ts';

const LastOwnedCardsRoot = styled(Horizontal).attrs({
  gap: 0,
})`
  padding: 2px;
  background: var(--surface-primary-nd-50, rgba(255, 255, 255, 0.07));
  border-radius: var(--intermediate-unit);
`;

const squareCss = css`
  width: 20px;
  height: 20px;
  margin-left: -10px;
`;

const CardAvatar = styled(GenericCardAvatar)`
  ${squareCss}
  &:first-child {
    margin-left: 0;
  }
`;

const MoreCards = styled(Horizontal).attrs({ gap: 0, center: true })`
  ${squareCss}
  backdrop-filter: blur(64px);
  background-color: var(--c-nd-50);
  border-radius: 50%;
  position: relative;
`;

const LabelXXS = styled.span`
  font: var(--t-label-xs);
  font-weight: bold;
  &.threeDigits {
    font: var(--unit) / var(--lh-10) var(--sorare-font);
  }
`;

type Props = {
  anyPlayer: LastOwnedCards_anyPlayer;
  withLabel?: boolean;
};

export const LastOwnedCardsByPlayer = ({
  anyPlayer,
  withLabel = false,
}: Props) => {
  if (!anyPlayer.lastOwnedCards.length) return null;

  const moreThan = anyPlayer.ownedCardsCount - anyPlayer.lastOwnedCards.length;

  return (
    <Tooltip
      title={
        <LazyAllLastOwnedCardsByPlayer
          playerSlug={anyPlayer.slug}
          displayName={anyPlayer.displayName}
          count={anyPlayer.ownedCardsCount}
        />
      }
    >
      <Horizontal gap={1} as="span">
        <LastOwnedCardsRoot>
          {anyPlayer.lastOwnedCards.map(card => {
            return (
              <CardAvatar key={card.slug} card={card} maxWidth={40} noTitle />
            );
          })}
          {anyPlayer.ownedCardsCount > anyPlayer.lastOwnedCards.length && (
            <MoreCards>
              <LabelXXS
                color="var(--c-nd-600)"
                className={classNames({ threeDigit: moreThan > 99 })}
              >
                +{moreThan}
              </LabelXXS>
            </MoreCards>
          )}
        </LastOwnedCardsRoot>
        {!!withLabel && (
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage
              defaultMessage="You own x{totalCount, number} {totalCount, plural, one {card} other {cards}}"
              values={{
                totalCount: anyPlayer.ownedCardsCount,
              }}
            />
          </LabelM>
        )}
      </Horizontal>
    </Tooltip>
  );
};

LastOwnedCardsByPlayer.fragments = {
  anyPlayer: gql`
    fragment LastOwnedCards_anyPlayer on AnyPlayerInterface {
      slug
      ownedCardsCount
      displayName
      lastOwnedCards(rarities: $lastOwnedCardsRarities) {
        slug
        rarityTyped
        ...CardAvatar_anyCard
      }
    }

    ${CardAvatar.fragments.anyCard}
  ` as TypedDocumentNode<LastOwnedCards_anyPlayer>,
};
